import React, { memo, useEffect, useMemo, useRef } from 'react';
import { devices } from '@common/constants/shared';
import { WebView } from 'react-native-webview';
import { View } from 'react-native';
import { isNil } from 'lodash';
import { useAppSelector } from '@common/redux/hooks';
import { deviceSelectedSelector } from '@common/redux/selectors/devices';
import { dimensionSelector } from '@common/redux/selectors/page';
import useAppDetail from '@common/hooks/app';
import { AppVersion, Platform as PlatformType } from 'click-types';
import { useCollections } from '@common/hooks/collections';

type Props = {
  appId: string;
  hasBorder?: boolean;
  handleIframeWidth?: (width: number) => void;
};

const IFrameShare = ({ appId, handleIframeWidth, hasBorder = true }: Props) => {
  const iframeRef: any = useRef();

  const deviceSelected = useAppSelector(deviceSelectedSelector);
  const dimension = useAppSelector(dimensionSelector);

  const { data } = useAppDetail({
    appId: appId,
    isShareScreen: true,
  });
  useCollections({ appId });

  const { version, platform } = data ?? {
    version: AppVersion.V3,
    platform: PlatformType.MOBILE,
  };

  const isV3 = version === AppVersion.V3 && platform == PlatformType.MOBILE;
  const isV4Responsive =
    version === AppVersion.V4 && platform === PlatformType.RESPONSIVE;

  // Use legacy calculation for V3 apps to maintain same look and feel as production
  const wrapperStyleEl = useMemo(() => {
    const deviceInfo = devices[deviceSelected];

    if (isV3 && dimension) {
      // Legacy V3 calculation: based on viewport dimension ratio (production method)
      const height = (dimension.height * 90) / 100;
      const width = height / deviceInfo.ratio;
      return { width, height };
    }

    // V4 calculation: use fixed device dimensions
    const { width, height } = deviceInfo;
    return { width, height };
  }, [deviceSelected, isV3, dimension]);

  const urlSearchParams = new URLSearchParams(window.location.search);
  const newUri = isV4Responsive
    ? `/preview-v4/${appId}?${urlSearchParams.toString()}`
    : `/preview/${appId}?${urlSearchParams.toString()}`;

  useEffect(() => {
    if (!isNil(iframeRef?.current) && data) {
      const currenFrame = iframeRef.current.frameRef;

      currenFrame.addEventListener('load', () => {
        setInterval(
          () => {
            const oldTitle = document?.title;
            const newTitle = currenFrame.contentWindow
              ? currenFrame?.contentWindow?.document?.title
              : currenFrame?.contentDocument?.title;
            if (oldTitle !== newTitle) {
              document.title = newTitle;
            }
          },
          isV4Responsive ? 1000 : 500
        );
      });
    }
  }, [iframeRef, data, isV4Responsive]);

  useEffect(() => {
    if (handleIframeWidth) {
      handleIframeWidth(wrapperStyleEl.width);
    }
  }, [wrapperStyleEl.width, handleIframeWidth]);

  return (
    <View
      style={{
        width: hasBorder ? wrapperStyleEl.width + 130 : wrapperStyleEl.width,
        height: hasBorder ? wrapperStyleEl.height + 150 : wrapperStyleEl.height,
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: hasBorder ? 30 : 0,
        backgroundColor: hasBorder ? '#E9EFF2' : 'transparent',
        borderWidth: hasBorder ? 30 : 0,
        borderColor: '#E9EFF2',
      }}
    >
      {isV3 ? (
        <View>
          <WebView
            source={{ uri: newUri }}
            style={{
              width: hasBorder
                ? wrapperStyleEl.width + 100
                : wrapperStyleEl.width,
              height: hasBorder
                ? wrapperStyleEl.height + 120
                : wrapperStyleEl.height,
              borderRadius: hasBorder ? 20 : 0,
              borderWidth: 0,
              borderColor: 'transparent',
            }}
            ref={iframeRef}
            scalesPageToFit={true}
            scrollEnabled={true}
            javaScriptEnabled={true}
            domStorageEnabled={true}
          />
        </View>
      ) : (
        <WebView
          source={{ uri: newUri }}
          style={{
            width: hasBorder
              ? wrapperStyleEl.width + 100
              : wrapperStyleEl.width,
            height: hasBorder
              ? wrapperStyleEl.height + 120
              : wrapperStyleEl.height,
            borderRadius: hasBorder ? 20 : 0,
            borderWidth: 0,
            borderColor: 'transparent',
          }}
          ref={iframeRef}
          scalesPageToFit={false}
          scrollEnabled={true}
          javaScriptEnabled={true}
          domStorageEnabled={true}
        />
      )}
    </View>
  );
};

export default memo(IFrameShare);
